import { Button, Divider, Layout, Typography } from 'antd'
import { DownOutlined, MenuFoldOutlined, MenuUnfoldOutlined } from '@ant-design/icons'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

import { createUseStyles, useTheme } from 'react-jss'
import { useIsAdmin, useLogout } from '../../redux/slices/session'
import { useSelectedPractice } from '../../context/PracticeContext'
import PracticeSelect, { usePracticeOptions } from '../Forms/PracticeSelect'
import ProfileLink from './ProfileLink'

const { Header } = Layout
const { Text } = Typography

const useStyles = createUseStyles({
  header: {
    display: 'flex',
    background: ({ theme }) => theme.brandBackground,
    padding: '0 16px',
    justifyContent: 'space-between',
    height: 91,
  },
  trigger: {
    fontSize: '18px',
    lineHeight: '64px',
    padding: '0 24px',
    cursor: 'pointer',
    transition: 'color 0.3s',
    '& :hover': {
      color: '#1890ff',
    },
  },
  rightActions: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    width: '80%',
    justifyContent: 'flex-end',
  },
  userColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    justifyContent: 'center',
    gap: 4,
    lineHeight: 'normal',
    minWidth: '50%',
    maxWidth: '80%',
    flex: 1,
  },
})

const usePracticeSelectorStyles = createUseStyles({
  wrapper: {
    position: 'relative',
    maxWidth: '100%',
  },
  practiceLink: {
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    maxWidth: '100%',
    border: 'none',
    background: 'none',
    padding: 0,
    font: 'inherit',
    color: 'inherit',
    '&:hover': {
      opacity: 0.7,
    },
  },
  practiceName: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  dropdown: {
    position: 'absolute',
    right: 0,
    top: '100%',
    marginTop: 4,
    width: 600,
    background: '#fff',
    borderRadius: 8,
    boxShadow: '0 6px 16px 0 rgba(0,0,0,0.08), 0 3px 6px -4px rgba(0,0,0,0.12), 0 9px 28px 8px rgba(0,0,0,0.05)',
    padding: 12,
    zIndex: 1050,
  },
  recentItem: {
    cursor: 'pointer',
    padding: '6px 12px',
    borderRadius: 4,
    border: 'none',
    background: 'none',
    width: '100%',
    textAlign: 'left',
    '&:hover': {
      backgroundColor: '#f5f5f5',
    },
  },
  recentLabel: {
    fontSize: 12,
    color: '#999',
    padding: '0 12px',
    marginBottom: 4,
  },
})

// Routes where the practice selector should be interactive (listing pages with practice filter)
const INTERACTIVE_ROUTES = [
  /^\/patients$/,
  /^\/notifications$/,
  /^\/transmissions$/,
  /^\/alerts$/,
  /^\/users$/,
  /^\/unreviewed-reports$/,
  /^\/pending-reports$/,
  /^\/reports$/,
  /^\/pre-authorizations$/,
  /^\/missed-transmissions$/,
  /^\/cycles$/,
  /^\/orders$/,
  /^\/office-checks$/,
  /^\/office-checks\/new$/,
  /^\/billing\/remote-billing-orders$/,
  /^\/billing\/office-billing-orders$/,
  /^\/billing\/office-authorizations$/,
  /^\/practices\/uploads$/,
  /^\/practices\/[^/]+\/missed-transmissions/,
  /^\/practices\/[^/]+\/reports/,
  /^\/practices\/[^/]+\/pre-authorizations/,
  /^\/practices\/[^/]+\/billing-orders/,
  /^\/practices\/[^/]+\/office-billing-orders/,
  /^\/practices\/[^/]+\/office-authorizations/,
  /^\/practices\/[^/]+\/office-checks/,
  /^\/practices\/[^/]+\/orders/,
  /^\/practices\/[^/]+\/cycles/,
  /^\/practices\/[^/]+\/uploads/,
]

function useIsInteractiveRoute() {
  const { pathname } = useLocation()
  return INTERACTIVE_ROUTES.some(re => re.test(pathname))
}

function HeaderPracticeSelector() {
  const { selectedPracticeId, setSelectedPracticeId, recentPracticeIds } = useSelectedPractice()
  const { options } = usePracticeOptions()
  const [panelOpen, setPanelOpen] = useState(false)
  const [selectOpen, setSelectOpen] = useState(false)
  const wrapperRef = useRef(null)
  const classes = usePracticeSelectorStyles()
  const interactive = useIsInteractiveRoute()

  const practiceMap = useMemo(
    () => new Map(options.map(o => [o.value, o.label])),
    [options],
  )

  const selectedName = selectedPracticeId ? practiceMap.get(selectedPracticeId) : null

  const recentPractices = useMemo(
    () => recentPracticeIds
      .filter(id => id !== selectedPracticeId && practiceMap.has(id))
      .map(id => ({ id, name: practiceMap.get(id) })),
    [recentPracticeIds, selectedPracticeId, practiceMap],
  )

  const handleSelect = useCallback(
    (id) => {
      setSelectedPracticeId(id || null)
      setSelectOpen(false)
      setPanelOpen(false)
    },
    [setSelectedPracticeId],
  )

  const handleToggle = useCallback(() => {
    setPanelOpen(prev => {
      if (!prev) {
        // Auto-open the Select dropdown only when no practice is selected
        if (!selectedPracticeId) {
          setTimeout(() => setSelectOpen(true), 0)
        }
      } else {
        setSelectOpen(false)
      }
      return !prev
    })
  }, [selectedPracticeId])

  // Close when clicking outside
  useEffect(() => {
    if (!panelOpen) return undefined
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        // Don't close if clicking inside the Select dropdown portal
        const selectDropdown = document.querySelector('.header-practice-dropdown')
        if (selectDropdown && selectDropdown.contains(e.target)) return
        setSelectOpen(false)
        setPanelOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [panelOpen])

  // Non-interactive: just show the practice name as plain text
  if (!interactive) {
    if (!selectedName) return null
    return (
      <span className={classes.practiceName} style={{ color: '#666' }}>
        {selectedName}
      </span>
    )
  }

  return (
    <div className={classes.wrapper} ref={wrapperRef}>
      <button type="button" className={classes.practiceLink} onClick={handleToggle}>
        <span
          className={classes.practiceName}
          style={{ color: selectedName ? undefined : '#999' }}
        >
          {selectedName || 'Select a practice'}
        </span>
        <DownOutlined style={{ fontSize: 10, color: '#999' }} />
      </button>
      {panelOpen && (
        <div className={classes.dropdown}>
          <PracticeSelect
            value={selectedPracticeId || undefined}
            onChange={handleSelect}
            width="100%"
            open={selectOpen}
            onDropdownVisibleChange={setSelectOpen}
            popupClassName="header-practice-dropdown"
          />
          {recentPractices.length > 0 && (
            <>
              <Divider style={{ margin: '8px 0' }} />
              <div className={classes.recentLabel}>Recent</div>
              {recentPractices.map(item => (
                <button
                  type="button"
                  key={item.id}
                  className={classes.recentItem}
                  onClick={() => handleSelect(item.id)}
                >
                  <Text ellipsis>{item.name}</Text>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  )
}

export default function AppHeader({ collapsed, handleToggleCollapsed }) {
  const theme = useTheme()
  const classes = useStyles({ theme })
  const logout = useLogout()
  const isAdmin = useIsAdmin()

  return (
    <Header className={classes.header}>
      {collapsed && (
        <MenuUnfoldOutlined
          className={classes.trigger}
          onClick={handleToggleCollapsed}
          style={{ color: theme.brandPrimary }}
        />
      )}
      {!collapsed && (
        <MenuFoldOutlined
          className={classes.trigger}
          onClick={handleToggleCollapsed}
          style={{ color: theme.brandPrimary }}
        />
      )}
      <div className={classes.rightActions}>
        <div className={classes.userColumn}>
          <ProfileLink />
          {isAdmin && <HeaderPracticeSelector />}
        </div>
        <Button
          type="primary"
          onClick={async () => {
            await logout()
          }}
        >
          Log Out
        </Button>
      </div>
    </Header>
  )
}

AppHeader.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  handleToggleCollapsed: PropTypes.func.isRequired,
}
